/**
 * boardTemplatesData.js
 * 32 predefined board templates for synagogue digital displays.
 * Seeded via TemplateManager → handleImportPredefined().
 */

// ── Visual Profiles ────────────────────────────────────────────────────────
const VISUAL_PROFILES = {
  neutral: { bgColor: '#fcfbfa', borderRadius: 16, fontFamily: 'rubik',           widgetShadow: 'subtle', widgetShadowColor: '#1e3a5f' },
  classic: { bgColor: '#f9f7f4', borderRadius: 10, fontFamily: 'frankRuhlLibre',  widgetShadow: 'subtle', widgetShadowColor: '#1e3a5f' },
  modern:  { bgColor: '#f8fafc', borderRadius: 24, fontFamily: 'heebo',           widgetShadow: 'subtle', widgetShadowColor: '#334155' },
  elegant: { bgColor: '#f5f0eb', borderRadius: 12, fontFamily: 'notoSerifHebrew', widgetShadow: 'medium', widgetShadowColor: '#1e3a5f' },
  minimal: { bgColor: '#ffffff', borderRadius: 20, fontFamily: 'assistant',       widgetShadow: 'none',   widgetShadowColor: '#000000' },
};

// ── Widget-level settings constants ────────────────────────────────────────
const WS_TIME      = { bgColor: '#ffffff', borderType: 'default' }; // Zmanim/Shabbat/Moad/Tefilot
const WS_NOTICE    = { bgColor: '#ffffff', borderType: 'default' }; // NoticeBoardWidget standard
const WS_NOTICE_EL = { bgColor: '#fffbf0', borderType: 'colored' }; // NoticeBoardWidget elegant
const WS_COMMUNITY = { bgColor: '#f0f9ff', borderType: 'default' }; // Contacts/Simcha/Emergency
const WS_PARNAS    = { bgColor: '',        borderType: 'none'    }; // ParnasWidget transparent
const WS_FINANCIAL = { bgColor: '#f8fafc', borderType: 'default' }; // Financial widgets
const WS_IMAGE     = { bgColor: '',        borderType: 'none'    }; // Image/Text widgets
// transparent + ללא מסגרת: שעון אנלוגי (עגול) צריך רקע שקוף כדי שפינות המלבן לא יחסמו את העיגול
const WS_CLOCK     = { bgColor: 'transparent', borderType: 'none' };

// ── Shared sample data ─────────────────────────────────────────────────────
const PRAYER_TIMES = [
  { type: 'fixed', time: '07:30', label: 'שחרית', icon: 'sunrise', accentColor: '#f59e0b', prayer: 'shacharit' },
  { type: 'fixed', time: '13:00', label: 'מנחה',  icon: 'sun',     accentColor: '#0ea5e9', prayer: 'mincha'    },
  { type: 'fixed', time: '20:00', label: 'ערבית', icon: 'moon',    accentColor: '#6366f1', prayer: 'maariv'    },
];

const SAMPLE_NOTICES = { notices: [
  { id: 'n1', content: '<p>ברוכים הבאים לבית הכנסת</p>' },
  { id: 'n2', content: '<p>נא לשמור על הסדר והניקיון</p>' },
  { id: 'n3', content: '<p>כאן אין מדברים בזמן התפילה</p>' },
  { id: 'n4', content: '<p>שאלות ופניות ניתן לפנות לגבאי בית הכנסת</p>' },
]};

const SAMPLE_CONTACTS = { contacts: [
  { id: 'c1', name: 'שם הגבאי', title: 'גבאי מניין מג"א', phone: '050-0000000', department: 'הנהלה' },
  { id: 'c2', name: 'משה משה',  title: 'אוצר הספרים',     phone: '050-0000001', department: 'מנהלה' },
  { id: 'c3', name: 'שם הגבאי', title: 'גבאי',            phone: '050-0000002', department: 'תפעול' },
]};

const SAMPLE_SIMCHAS = { simchas: [
  { id: 's1', name: 'בר מצווה', date: '2026-04-05', type: 'bar_mitzvah', family: 'משפחת כהן', description: 'מזל טוב!' },
  { id: 's2', name: 'חתונה',    date: '2026-04-12', type: 'wedding',     family: 'משפחת לוי', description: 'בשעה טובה!' },
]};

const SAMPLE_EMERGENCY = { volunteers: {
  medic:     [{ id: 'e1', name: 'ישראל כהן', phone: '050-1000000' }],
  paramedic: [],
  maar:      [{ id: 'e2', name: 'מוטי לוי',  phone: '050-2000000' }],
}};

const PARNAS_BASE_PROPS = {
  title: 'פרנס הקהילה', showTitle: false,
  parnasType: 'maor', parnasTypeIcon: 'star',
  sponsors: [], placeholderName: 'לתרומות ניתן לפנות לגבאי בית הכנסת', placeholderNote: '',
};

const FINANCIAL_DATA = {
  incomeData:   [{ id: 'donations', label: 'תרומות',   value: 60000, color: '#1e3a5f' }, { id: 'membership', label: 'דמי חבר', value: 30000, color: '#2d5a8f' }],
  expensesData: [{ id: 'maintenance', label: 'תחזוקה', value: 25000, color: '#ef4444' }, { id: 'electricity', label: 'חשמל',   value: 12000, color: '#f59e0b' }],
};

const EXPENSES_DATA = { categories: [
  { name: 'חשמל ומים',       amount: 4000, color: '#00C9E0' },
  { name: 'ניקיון',          amount: 2500, color: '#4ADE80' },
  { name: 'תחזוקה',         amount: 3000, color: '#F59E0B' },
  { name: 'תשלומי הלוואה',  amount: 8000, color: '#818CF8' },
]};

const SHABBAT_CUSTOM_TIMES = [
  { type: 'fixed',    time: '08:00', label: 'שחרית שבת',    icon: 'sunrise', accentColor: '#f59e0b', day: 'shabbat' },
  { type: 'fixed',    time: '16:00', label: 'מנחה שבת',     icon: 'sun',     accentColor: '#0ea5e9', day: 'shabbat' },
  { type: 'relative', baseTime: 'shabbatSunset', offset: 40, unit: 'minutes',
    label: 'מעריב מוצ"ש', icon: 'moon', accentColor: '#6366f1', day: 'shabbat' },
];

const SHABBAT_BASE = {
  title: 'זמני שבת', showTitle: true,
  customTimes: SHABBAT_CUSTOM_TIMES, drashot: [],
  entryIcon: 'ShabbatCandles', exitIcon: 'MoonStar',
};

// ── Widget helper ──────────────────────────────────────────────────────────
function w(widgetId, componentKey, templateId, x, y, ww, h, props, widgetSettings = WS_TIME) {
  return { widgetId, componentKey, templateId, dashboardLayout: { x, y, w: ww, h }, props: props || {}, widgetSettings };
}

// ── Shared prop builders ───────────────────────────────────────────────────
const zTL  = (title = 'זמני היום')  => ({ title, showTitle: true,  customTimes: [] });           // Zmanim Timeline
const tef  = (title = 'תפילות')     => ({ title, showTitle: true,  customTimes: PRAYER_TIMES });
const noti = (title = 'הודעות')     => ({ title, showTitle: true,  data: SAMPLE_NOTICES });
const cont = (title = 'אנשי קשר')  => ({ title, showTitle: true,  data: SAMPLE_CONTACTS });
const simc = (title = 'שמחות')      => ({ title, showTitle: true,  data: SAMPLE_SIMCHAS });
const emer = (title = 'חירום')      => ({ title, showTitle: true,  data: SAMPLE_EMERGENCY });
const fin  = (title = 'יעדים כספיים') => ({ title, showTitle: true, data: FINANCIAL_DATA });
const exp  = (title = 'ניתוח הוצאות') => ({ title, showTitle: true, data: EXPENSES_DATA, freeText: '' });
const parnas = (extra = {}) => ({ ...PARNAS_BASE_PROPS, ...extra });
const moad = (title = 'זמני החג')   => ({ title, showTitle: true,  customTimes: [], days: [] });
const s = (extra = {}) => ({ ...SHABBAT_BASE, ...extra });

// ── Settings builder ───────────────────────────────────────────────────────
const settings = (profile) => ({ ...VISUAL_PROFILES[profile], bgImage: '' });

// ── Clock template per visual profile ─────────────────────────────────────
// Each profile has a clock template that matches its aesthetic:
//   neutral  → DigitalClassic  (clean digital face, works with modern sans-serif)
//   classic  → ClassicWood     (warm wooden analog clock)
//   modern   → DarkNavy        (sleek dark digital, matches modern dark accents)
//   elegant  → GoldenSynagogue (ornate golden analog, matches elegant serif)
//   minimal  → MarbleWhite     (clean marble analog, matches minimal white)
const CLOCK_TPL = {
  neutral: 'DigitalClassic',
  classic: 'ClassicWood',
  modern:  'DarkNavy',
  elegant: 'GoldenSynagogue',
  minimal: 'MarbleWhite',
};

// ══════════════════════════════════════════════════════════════════════════════
// 32 BOARD TEMPLATES
// ══════════════════════════════════════════════════════════════════════════════
export const BOARD_TEMPLATES = [

  // ─────────────────────────────────────────────────────────────────────────
  // A. יום חול (T01–T09)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0001-4d5e-b6f7-a8b9c0d1e201',
    name: 'יום חול מלא',
    category: 'weekday',
    concept: 'לוח יום-יום שלם עם כל מידע הקהילה — זמנים, תפילות, הודעות, שבת, פרנס, אנשי קשר ושמחות.',
    whenToUse: 'מתאים לבית כנסת עם קהילה פעילה שרוצה לוח מרכזי אחד לכל ימות השבוע.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון קיר דיגיטלי בראש העמודה הימנית' },
      { name: 'ציר זמן', reason: 'מציג את כל הזמנים היומיים בצורה גרפית' },
      { name: 'תפילות', reason: 'שחרית, מנחה וערבית עם שעות קבועות' },
      { name: 'הודעות', reason: 'הודעות לקהילה' },
      { name: 'שבת', reason: 'תזכורת לשבת הקרובה' },
      { name: 'פרנס', reason: 'תורן השבוע' },
      { name: 'אנשי קשר', reason: 'פרטי הגבאים' },
      { name: 'שמחות', reason: 'אירועים קרובים בקהילה' },
    ],
    suggestedWith: ['bt-0010-4d5e-b6f7-a8b9c0d1e210', 'bt-0005-4d5e-b6f7-a8b9c0d1e205'],
    settings: settings('neutral'),
    widgets: [
      w('w01-01', 'ZmanimWidget',     'Normal',          0,  0, 6, 28, zTL(),  WS_TIME),
      w('w01-02', 'TefilotWidget',    'Modern',          6,  0, 6, 14, tef(),  WS_TIME),
      w('w01-03', 'NoticeBoardWidget','Board',           6, 14, 6, 14, noti(), WS_NOTICE),
      w('w01-04', 'ShabbatWidget',    'Card',           12,  0, 6, 14, s(),    WS_TIME),
      w('w01-05', 'ParnasWidget',     'WeekAhead',      12, 14, 6, 14, parnas(), WS_PARNAS),
      // שעון בראש העמודה הימנית — מחליף את תמונת הפלייסהולדר
      w('w01-clk','ClockWidget',      CLOCK_TPL.neutral, 18,  0, 6, 14, {},    WS_CLOCK),
      w('w01-06', 'ContactsWidget',   'List',           18, 14, 6,  8, cont(), WS_COMMUNITY),
      w('w01-07', 'SimchaWidget',     'Cards',          18, 22, 6,  6, simc(), WS_COMMUNITY),
    ],
  },

  {
    id: 'bt-0002-4d5e-b6f7-a8b9c0d1e202',
    name: 'יום חול — תפילות במרכז',
    category: 'weekday',
    concept: 'מעמיד את לוח התפילות במרכז המסך — מתאים לבית כנסת שהתפילות הן מוקד ההתעניינות.',
    whenToUse: 'מסכים ממוקמים ליד הכניסה או קרוב לבימה, שבהם חשוב מאוד לדעת מתי התפילה הבאה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בראש עמודת הזמנים — השעה הנוכחית בולטת' },
      { name: 'זמנים', reason: 'רשימת זמנים בצד שמאל' },
      { name: 'תפילות', reason: 'עמודות שחרית/מנחה/ערבית בולטות במרכז' },
      { name: 'הודעות', reason: 'הודעות קהילה בצד' },
      { name: 'שבת', reason: 'זמני שבת בפינה' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0008-4d5e-b6f7-a8b9c0d1e208'],
    settings: settings('neutral'),
    widgets: [
      // שעון בחצי העליון של עמודת הזמנים; ZmanimWidget נדחק לחצי התחתון
      w('w02-clk','ClockWidget',      CLOCK_TPL.neutral, 0,  0,  6, 14, {},    WS_CLOCK),
      w('w02-01', 'ZmanimWidget',     'Normal',          0, 14,  6, 14, zTL(), WS_TIME),
      w('w02-02', 'TefilotWidget',    'Columns',         6,  0, 12, 28, tef(), WS_TIME),
      w('w02-03', 'NoticeBoardWidget','Board',          18,  0,  6, 14, noti(), WS_NOTICE),
      w('w02-04', 'ShabbatWidget',    'Times',          18, 14,  6, 14, s(),   WS_TIME),
    ],
  },

  {
    id: 'bt-0003-4d5e-b6f7-a8b9c0d1e203',
    name: 'יום חול — פרנס בולט',
    category: 'weekday',
    concept: 'הפרנס תופס מעמד מרכזי בולט בצד שמאל — מתאים לבתי כנסת שמדגישים את תרומות הקהילה.',
    whenToUse: 'מסכים המוצבים בחדר תפילה ראשי, כאשר רוצים להוקיר ולפרסם את הפרנס.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון עץ קלאסי בין עמודת הזמנים לעמודת התפילות' },
      { name: 'פרנס', reason: 'תפרוסת רחבה ובולטת' },
      { name: 'זמנים', reason: 'זמנים מלאים בעמודה המרכזית' },
      { name: 'תפילות', reason: 'שעות התפילה בעמודה הצרה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
    ],
    suggestedWith: ['bt-0011-4d5e-b6f7-a8b9c0d1e211', 'bt-0024-4d5e-b6f7-a8b9c0d1e224'],
    settings: settings('classic'),
    widgets: [
      w('w03-01', 'ParnasWidget',     'Spread',          0,  0, 10, 28, parnas(), WS_PARNAS),
      // ZmanimWidget מצומצם ל-w:4 כדי לפנות מקום לשעון בחצי העליון
      w('w03-02', 'ZmanimWidget',     'Normal',         10,  0,  4, 28, zTL(),    WS_TIME),
      w('w03-clk','ClockWidget',      CLOCK_TPL.classic, 14,  0,  4, 14, {},      WS_CLOCK),
      w('w03-03', 'TefilotWidget',    'Modern',          14, 14,  4, 14, tef(),   WS_TIME),
      // הודעות מורחבות לגובה מלא עכשיו שתפילות עברו לעמודה האמצעית
      w('w03-04', 'NoticeBoardWidget','Board',           18,  0,  6, 28, noti(),  WS_NOTICE),
    ],
  },

  {
    id: 'bt-0004-4d5e-b6f7-a8b9c0d1e204',
    name: 'יום חול — לוח מודעות ראשי',
    category: 'weekday',
    concept: 'לוח המודעות מוצג בפורמט ספוטלייט גדול ובולט במרכז — הודעה אחת בכל פעם ומחליפה אוטומטית.',
    whenToUse: 'מסכים גדולים בלובי או בחדרי המתנה, כאשר ההודעות הן הראשוניות בחשיבותן.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'זמנים בצד' },
      { name: 'הודעות ספוטלייט', reason: 'הודעה מרכזית מוחלפת אוטומטית' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'שבת', reason: 'תזכורת שבת' },
    ],
    suggestedWith: ['bt-0026-4d5e-b6f7-a8b9c0d1e226', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('neutral'),
    widgets: [
      w('w04-clk','ClockWidget',      CLOCK_TPL.neutral, 0,  0,  6, 14, {},    WS_CLOCK),
      w('w04-01', 'ZmanimWidget',     'Normal',          0, 14,  6, 14, zTL(), WS_TIME),
      w('w04-02', 'NoticeBoardWidget','Spotlight',       6,  0, 12, 28, noti(), WS_NOTICE),
      w('w04-03', 'TefilotWidget',    'Modern',         18,  0,  6, 14, tef(),  WS_TIME),
      w('w04-04', 'ShabbatWidget',    'Times',          18, 14,  6, 14, s(),    WS_TIME),
    ],
  },

  {
    id: 'bt-0005-4d5e-b6f7-a8b9c0d1e205',
    name: 'יום חול — עם שמחות',
    category: 'weekday',
    concept: 'לוח יום-יום המדגיש שמחות קרובות לצד הזמנים וההודעות — פרנס שבועי בולט בצד.',
    whenToUse: 'בתי כנסת שמעוניינים לשתף עם הקהילה אירועים שמחים כגון ברית, בר מצווה וחתונה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בחצי העליון של עמודת הפרנס' },
      { name: 'ציר זמן', reason: 'זמני היום גרפית' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
      { name: 'שבת', reason: 'תזכורת שבת' },
      { name: 'שמחות', reason: 'אירועים שמחים קרובים' },
      { name: 'פרנס שבועי', reason: 'תורן השבוע' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0015-4d5e-b6f7-a8b9c0d1e215'],
    settings: settings('neutral'),
    widgets: [
      w('w05-01', 'ZmanimWidget',     'Normal',          0,  0, 6, 28, zTL(),    WS_TIME),
      w('w05-02', 'TefilotWidget',    'Modern',          6,  0, 6, 14, tef(),    WS_TIME),
      w('w05-03', 'NoticeBoardWidget','Board',           6, 14, 6, 14, noti(),   WS_NOTICE),
      w('w05-04', 'ShabbatWidget',    'Times',          12,  0, 6, 14, s(),      WS_TIME),
      w('w05-05', 'SimchaWidget',     'List',           12, 14, 6, 14, simc(),   WS_COMMUNITY),
      // שעון בחצי עליון, ParnasWidget נדחק לחצי התחתון
      w('w05-clk','ClockWidget',      CLOCK_TPL.neutral, 18,  0, 6, 14, {},      WS_CLOCK),
      w('w05-06', 'ParnasWidget',     'WeekAhead',       18, 14, 6, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0006-4d5e-b6f7-a8b9c0d1e206',
    name: 'יום חול — עם חירום',
    category: 'weekday',
    concept: 'לוח יום-יום שמשלב מידע חירום ואנשי קשר לטיפול בדחיפות — מתאים לבית כנסת עם נוהל מד"א.',
    whenToUse: 'מסכים הפונים לציבור, בייחוד בבתי כנסת עם הרבה מתפללים שחשוב שידעו למי לפנות.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'זמני היום' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'כוננות חירום', reason: 'אנשי חירום ומד"א' },
      { name: 'הודעות', reason: 'הודעות קהילה בולטות' },
      { name: 'שבת', reason: 'תזכורת שבת' },
      { name: 'אנשי קשר', reason: 'פרטי גבאים' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0025-4d5e-b6f7-a8b9c0d1e225'],
    settings: settings('neutral'),
    widgets: [
      w('w06-clk','ClockWidget',      CLOCK_TPL.neutral, 0,  0,  6, 14, {},    WS_CLOCK),
      w('w06-01', 'ZmanimWidget',     'Normal',          0, 14,  6, 14, zTL(), WS_TIME),
      w('w06-02', 'TefilotWidget',    'Modern',          6,  0,  6, 14, tef(),  WS_TIME),
      w('w06-03', 'EmergencyWidget',  'List',            6, 14,  6, 14, emer(), WS_COMMUNITY),
      w('w06-04', 'NoticeBoardWidget','Callout',        12,  0,  6, 28, noti(), WS_NOTICE),
      w('w06-05', 'ShabbatWidget',    'Times',          18,  0,  6, 14, s(),    WS_TIME),
      w('w06-06', 'ContactsWidget',   'List',           18, 14,  6, 14, cont(), WS_COMMUNITY),
    ],
  },

  {
    id: 'bt-0007-4d5e-b6f7-a8b9c0d1e207',
    name: 'יום חול — קהילה',
    category: 'weekday',
    concept: 'מדגיש את אנשי הקהילה — אנשי קשר, כוננות חירום ושמחות — לצד זמנים ופרנס.',
    whenToUse: 'בתי כנסת שרוצים ליצור תחושת קהילתיות חזקה ולהכיר פנים לחברי הקהילה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בחצי העליון של עמודת הפרנס' },
      { name: 'זמנים', reason: 'זמני היום' },
      { name: 'אנשי קשר', reason: 'גבאים ואנשי הנהלה' },
      { name: 'כוננות חירום', reason: 'כוננות מד"א' },
      { name: 'שמחות', reason: 'שמחות קהילה' },
      { name: 'הודעות', reason: 'הודעות כלליות' },
      { name: 'פרנס שבועי', reason: 'תורן השבוע' },
    ],
    suggestedWith: ['bt-0025-4d5e-b6f7-a8b9c0d1e225', 'bt-0005-4d5e-b6f7-a8b9c0d1e205'],
    settings: settings('neutral'),
    widgets: [
      w('w07-01', 'ZmanimWidget',     'Normal',          0,  0, 6, 28, zTL(),    WS_TIME),
      w('w07-02', 'ContactsWidget',   'List',            6,  0, 6, 14, cont(),   WS_COMMUNITY),
      w('w07-03', 'EmergencyWidget',  'List',            6, 14, 6, 14, emer(),   WS_COMMUNITY),
      w('w07-04', 'SimchaWidget',     'Cards',          12,  0, 6, 14, simc(),   WS_COMMUNITY),
      w('w07-05', 'NoticeBoardWidget','Board',          12, 14, 6, 14, noti(),   WS_NOTICE),
      w('w07-clk','ClockWidget',      CLOCK_TPL.neutral, 18,  0, 6, 14, {},      WS_CLOCK),
      w('w07-06', 'ParnasWidget',     'WeekAhead',       18, 14, 6, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0008-4d5e-b6f7-a8b9c0d1e208',
    name: 'יום חול — שלוש עמודות',
    category: 'weekday',
    concept: 'שלוש עמודות שוות: זמנים, תפילות בעמודות, ולוח הודעות — עיצוב מסודר ומאוזן.',
    whenToUse: 'מסכים רחבים שרוצים הפרדה ויזואלית ברורה בין מידע הזמנים, התפילות וההודעות.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון כהה ומודרני בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'עמודה שמאלית' },
      { name: 'תפילות', reason: 'שחרית/מנחה/ערבית בעמודות נפרדות' },
      { name: 'הודעות', reason: 'הודעות בפורמט פתקיות' },
      { name: 'שבת', reason: 'תזכורת שבת בפינה' },
    ],
    suggestedWith: ['bt-0002-4d5e-b6f7-a8b9c0d1e202', 'bt-0013-4d5e-b6f7-a8b9c0d1e213'],
    settings: settings('modern'),
    widgets: [
      w('w08-clk','ClockWidget',      CLOCK_TPL.modern,  0,  0, 8, 14, {},    WS_CLOCK),
      w('w08-01', 'ZmanimWidget',     'Normal',          0, 14, 8, 14, zTL(), WS_TIME),
      w('w08-02', 'TefilotWidget',    'Columns',         8,  0, 8, 28, tef(), WS_TIME),
      w('w08-03', 'NoticeBoardWidget','Sticky',         16,  0, 8, 14, noti(), WS_NOTICE),
      w('w08-04', 'ShabbatWidget',    'Times',          16, 14, 8, 14, s(),    WS_TIME),
    ],
  },

  {
    id: 'bt-0009-4d5e-b6f7-a8b9c0d1e209',
    name: 'יום חול — כלכלה',
    category: 'weekday',
    concept: 'לוח יום-יום המשלב נתונים כלכליים — יעדי תקציב וניתוח הוצאות — לצד פרנס ותפילות.',
    whenToUse: 'ועדי בתי כנסת שרוצים שקיפות כלכלית ולידע את הקהילה במצב התקציב.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בחצי העליון של עמודת הפרנס' },
      { name: 'ציר זמן', reason: 'זמני היום' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
      { name: 'יעדים כספיים', reason: 'עמידה ביעדי תרומות' },
      { name: 'ניתוח הוצאות', reason: 'פירוט הוצאות הקהילה' },
      { name: 'פרנס', reason: 'תורן השבוע' },
    ],
    suggestedWith: ['bt-0027-4d5e-b6f7-a8b9c0d1e227', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('neutral'),
    widgets: [
      w('w09-01', 'ZmanimWidget',           'Normal',          0,  0, 6, 28, zTL(),    WS_TIME),
      w('w09-02', 'TefilotWidget',          'Modern',          6,  0, 6, 14, tef(),    WS_TIME),
      w('w09-03', 'NoticeBoardWidget',      'Board',           6, 14, 6, 14, noti(),   WS_NOTICE),
      w('w09-04', 'FinancialGaugeWidget',   'Gauge',          12,  0, 6, 14, fin(),    WS_FINANCIAL),
      w('w09-05', 'ExpensesAnalyticsWidget','Pie',            12, 14, 6, 14, exp(),    WS_FINANCIAL),
      w('w09-clk','ClockWidget',            CLOCK_TPL.neutral, 18,  0, 6, 14, {},      WS_CLOCK),
      w('w09-06', 'ParnasWidget',           'Tiles',           18, 14, 6, 14, parnas(), WS_PARNAS),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // B. שבת (T10–T16)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0010-4d5e-b6f7-a8b9c0d1e210',
    name: 'שבת מלאה',
    category: 'shabbat',
    concept: 'לוח שבת שלם ואלגנטי — ציר זמן, פרטי שבת, הודעות ופרנס — בעיצוב חגיגי.',
    whenToUse: 'מסכים ליד כניסת בית הכנסת בשבת, כאשר חשוב להציג את כל מידע השבת.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב מפואר בראש עמודת הזמנים' },
      { name: 'ציר זמן', reason: 'זמני שבת ויום' },
      { name: 'שבת', reason: 'פרטי שבת מלאים' },
      { name: 'הודעות', reason: 'הודעות לשבת עם עיצוב אורנמנט' },
      { name: 'פרנס', reason: 'פרנס השבת' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0021-4d5e-b6f7-a8b9c0d1e221'],
    settings: settings('elegant'),
    widgets: [
      w('w10-clk','ClockWidget',      CLOCK_TPL.elegant, 0,  0, 8, 14, {},    WS_CLOCK),
      w('w10-01', 'ZmanimWidget',     'Normal',          0, 14, 8, 14, zTL(), WS_TIME),
      w('w10-02', 'ShabbatWidget',    'Card',            8,  0, 8, 28, s(),   WS_TIME),
      w('w10-03', 'NoticeBoardWidget','Ornament',       16,  0, 8, 14, noti(), WS_NOTICE_EL),
      w('w10-04', 'ParnasWidget',     'Tiles',          16, 14, 8, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0011-4d5e-b6f7-a8b9c0d1e211',
    name: 'שבת — פרנס גדול',
    category: 'shabbat',
    concept: 'הפרנס תופס את מרבית המסך — לבית כנסת שהוקרת התורם היא בראש סדר העדיפויות.',
    whenToUse: 'שבתות מיוחדות כגון שבת פרשה בה יש עלייה לתורה, ברית מילה ועוד.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב מפואר בראש עמודת השבת' },
      { name: 'פרנס', reason: 'תפרוסת רחבה מאוד' },
      { name: 'שבת', reason: 'פרטי שבת מלאים' },
      { name: 'זמנים', reason: 'זמנים בעמודה צרה' },
      { name: 'הודעות', reason: 'הודעות לשבת' },
    ],
    suggestedWith: ['bt-0003-4d5e-b6f7-a8b9c0d1e203', 'bt-0024-4d5e-b6f7-a8b9c0d1e224'],
    settings: settings('elegant'),
    widgets: [
      w('w11-01', 'ParnasWidget',     'Spread',          0,  0, 10, 28, parnas(), WS_PARNAS),
      // שעון בחצי עליון של עמודת השבת
      w('w11-clk','ClockWidget',      CLOCK_TPL.elegant, 10,  0,  8, 14, {},      WS_CLOCK),
      w('w11-02', 'ShabbatWidget',    'Card',            10, 14,  8, 14, s(),     WS_TIME),
      w('w11-03', 'ZmanimWidget',     'Normal',          18,  0,  6, 14, zTL(),   WS_TIME),
      w('w11-04', 'NoticeBoardWidget','Board',           18, 14,  6, 14, noti(),  WS_NOTICE_EL),
    ],
  },

  {
    id: 'bt-0012-4d5e-b6f7-a8b9c0d1e212',
    name: 'שבת — פרנס שבועי',
    category: 'shabbat',
    concept: 'פרנס שבועי בולט לצד פרטי שבת מלאים — עיצוב קלאסי מכובד.',
    whenToUse: 'בתי כנסת שמפרסמים פרנס שבועי ורוצים לתת לו נוכחות ויזואלית מכובדת.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון עץ קלאסי בראש עמודת הפרנס' },
      { name: 'פרנס שבועי', reason: 'תורן השבוע הנוכחי' },
      { name: 'שבת', reason: 'פרטי שבת מלאים' },
      { name: 'זמנים', reason: 'זמנים נוספים' },
      { name: 'הודעות', reason: 'הודעות לשבת באורנמנט' },
    ],
    suggestedWith: ['bt-0010-4d5e-b6f7-a8b9c0d1e210', 'bt-0016-4d5e-b6f7-a8b9c0d1e216'],
    settings: settings('classic'),
    widgets: [
      w('w12-clk','ClockWidget',      CLOCK_TPL.classic,  0,  0,  6, 14, {},      WS_CLOCK),
      w('w12-01', 'ParnasWidget',     'WeekAhead',        0, 14,  6, 14, parnas(), WS_PARNAS),
      w('w12-02', 'ShabbatWidget',    'Card',             6,  0,  9, 28, s(),      WS_TIME),
      w('w12-03', 'ZmanimWidget',     'Normal',          15,  0,  9, 14, zTL(),    WS_TIME),
      w('w12-04', 'NoticeBoardWidget','Ornament',        15, 14,  9, 14, noti(),   WS_NOTICE),
    ],
  },

  {
    id: 'bt-0013-4d5e-b6f7-a8b9c0d1e213',
    name: 'שבת מינימלי',
    category: 'shabbat',
    concept: 'שלוש עמודות נקיות — שבת, זמנים והודעות — בעיצוב מינימלי מאוד ומרגיע.',
    whenToUse: 'בתי כנסת עם סגנון עיצוב מינימלי, או כאשר רוצים מסך נקי ללא עומס ויזואלי.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון שיש לבן נקי בראש עמודת הזמנים' },
      { name: 'שבת', reason: 'פרטי שבת בולטים' },
      { name: 'זמנים', reason: 'רשימת זמנים' },
      { name: 'הודעות', reason: 'הודעות בפורמט מינימלי' },
    ],
    suggestedWith: ['bt-0029-4d5e-b6f7-a8b9c0d1e229', 'bt-0030-4d5e-b6f7-a8b9c0d1e230'],
    settings: settings('minimal'),
    widgets: [
      w('w13-01', 'ShabbatWidget',    'Card',             0,  0, 8, 28, s(),    WS_TIME),
      w('w13-clk','ClockWidget',      CLOCK_TPL.minimal,  8,  0, 8, 14, {},     WS_CLOCK),
      w('w13-02', 'ZmanimWidget',     'Normal',           8, 14, 8, 14, zTL(),  WS_TIME),
      w('w13-03', 'NoticeBoardWidget','Minimal',         16,  0, 8, 28, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0014-4d5e-b6f7-a8b9c0d1e214',
    name: 'שבת אלגנטי',
    category: 'shabbat',
    concept: 'עיצוב שבת אלגנטי עם שבת מודרני ורחב במרכז, פרנס והודעות בצד.',
    whenToUse: 'מסכים בבית כנסת ייצוגי שרוצה עיצוב מכובד ואלגנטי לשבת.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב אלגנטי בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'בעמודה צרה' },
      { name: 'שבת מודרני', reason: 'עיצוב עשיר ומרחיב' },
      { name: 'פרנס', reason: 'אריחי פרנסים' },
      { name: 'הודעות', reason: 'הודעות בגבול בולט' },
    ],
    suggestedWith: ['bt-0010-4d5e-b6f7-a8b9c0d1e210', 'bt-0018-4d5e-b6f7-a8b9c0d1e218'],
    settings: settings('elegant'),
    widgets: [
      w('w14-clk','ClockWidget',      CLOCK_TPL.elegant,  0,  0,  6, 14, {},    WS_CLOCK),
      w('w14-01', 'ZmanimWidget',     'Normal',           0, 14,  6, 14, zTL(), WS_TIME),
      w('w14-02', 'ShabbatWidget',    'Modern',           6,  0, 12, 28, s(),   WS_TIME),
      w('w14-03', 'ParnasWidget',     'Tiles',           18,  0,  6, 14, parnas(), WS_PARNAS),
      w('w14-04', 'NoticeBoardWidget','Callout',         18, 14,  6, 14, noti(),  WS_NOTICE_EL),
    ],
  },

  {
    id: 'bt-0015-4d5e-b6f7-a8b9c0d1e215',
    name: 'שבת — עם שמחות',
    category: 'shabbat',
    concept: 'שבת מלאה עם בולטות לשמחות — ברית, בר מצווה וחתונה שמתקיימים בשבת.',
    whenToUse: 'שבתות שבהן יש שמחה בקהילה — הלוח מציג את השמחה בצד שבת.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון עץ קלאסי בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'זמני שבת' },
      { name: 'שבת', reason: 'פרטי שבת מלאים' },
      { name: 'שמחות', reason: 'שמחות השבת' },
      { name: 'הודעות', reason: 'הודעות לשבת' },
    ],
    suggestedWith: ['bt-0005-4d5e-b6f7-a8b9c0d1e205', 'bt-0010-4d5e-b6f7-a8b9c0d1e210'],
    settings: settings('classic'),
    widgets: [
      w('w15-clk','ClockWidget',      CLOCK_TPL.classic,  0,  0, 6, 14, {},    WS_CLOCK),
      w('w15-01', 'ZmanimWidget',     'Normal',           0, 14, 6, 14, zTL(), WS_TIME),
      w('w15-02', 'ShabbatWidget',    'Card',             6,  0, 9, 28, s(),   WS_TIME),
      w('w15-03', 'SimchaWidget',     'Cards',           15,  0, 9, 14, simc(), WS_COMMUNITY),
      w('w15-04', 'NoticeBoardWidget','Board',           15, 14, 9, 14, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0016-4d5e-b6f7-a8b9c0d1e216',
    name: 'שבת — פרנסים שנתיים',
    category: 'shabbat',
    concept: 'רשת הפרנסים השנתיים תופסת את רוב המסך — מתאים לתצוגה שנתית של כל תורמי השנה.',
    whenToUse: 'בתי כנסת שמנהלים מערכת פרנסים שנתית ורוצים להציג את כל התורמים לאורך השנה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב אלגנטי בין הפרנסים לעמודת הזמנים' },
      { name: 'פרנסים שנתיים', reason: 'כל פרנסי השנה בפריסה רחבה' },
      { name: 'זמנים קבוע', reason: 'זמנים בפורמט קומפקטי' },
      { name: 'שבת', reason: 'זמני שבת' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות לשבת' },
    ],
    suggestedWith: ['bt-0024-4d5e-b6f7-a8b9c0d1e224', 'bt-0003-4d5e-b6f7-a8b9c0d1e203'],
    settings: settings('elegant'),
    widgets: [
      // ParnasWidget מצומצם ל-w:8 (היה 12) כדי לאפשר עמודת שעון+זמנים ברוחב 4
      w('w16-01', 'ParnasWidget',     'WeeklyYear',      0,  0,  8, 28, parnas(), WS_PARNAS),
      w('w16-clk','ClockWidget',      CLOCK_TPL.elegant, 8,  0,  4, 14, {},       WS_CLOCK),
      w('w16-02', 'ZmanimWidget',     'Normal',          8, 14,  4, 14, zTL(),    WS_TIME),
      w('w16-03', 'ShabbatWidget',    'Times',          12,  0,  6, 14, s(),      WS_TIME),
      w('w16-04', 'TefilotWidget',    'Modern',         12, 14,  6, 14, tef(),    WS_TIME),
      // הודעות מורחבות לגובה מלא
      w('w16-05', 'NoticeBoardWidget','Board',          18,  0,  6, 28, noti(),   WS_NOTICE_EL),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // C. מועדים (T17–T20)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0017-4d5e-b6f7-a8b9c0d1e217',
    name: 'מועד כללי',
    category: 'holiday',
    concept: 'לוח חג כללי עם ציר זמן, זמני החג, תפילות והודעות — פריסה מאוזנת ומסודרת.',
    whenToUse: 'ימי חג כגון ראש השנה, יום כיפור, פסח וסוכות — לוח שמתאים לכל חג.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון דיגיטלי בראש עמודת הזמנים' },
      { name: 'ציר זמן', reason: 'זמני החג גרפית' },
      { name: 'זמני מועד', reason: 'כניסה ויציאה לכל יום' },
      { name: 'תפילות', reason: 'שעות תפילת החג' },
      { name: 'הודעות', reason: 'הודעות לחג' },
    ],
    suggestedWith: ['bt-0018-4d5e-b6f7-a8b9c0d1e218', 'bt-0019-4d5e-b6f7-a8b9c0d1e219'],
    settings: settings('neutral'),
    widgets: [
      w('w17-clk','ClockWidget',      CLOCK_TPL.neutral, 0,  0,  8, 14, {},    WS_CLOCK),
      w('w17-01', 'ZmanimWidget',     'Normal',          0, 14,  8, 14, zTL(), WS_TIME),
      w('w17-02', 'MoadWidget',       'Modern',          8,  0, 10, 28, moad(), WS_TIME),
      w('w17-03', 'TefilotWidget',    'Modern',         18,  0,  6, 14, tef(),  WS_TIME),
      w('w17-04', 'NoticeBoardWidget','Board',          18, 14,  6, 14, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0018-4d5e-b6f7-a8b9c0d1e218',
    name: 'מועד — עם פרנס',
    category: 'holiday',
    concept: 'לוח חג אלגנטי עם מועד בעיצוב כהה, פרנס וציר זמן — חגיגי ומכובד.',
    whenToUse: 'חגים שבהם יש פרנס מיוחד לכבוד החג — ראש השנה, יום כיפור ועוד.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב אלגנטי בראש עמודת הזמנים' },
      { name: 'זמני מועד', reason: 'עיצוב אלגנטי כהה' },
      { name: 'ציר זמן', reason: 'זמני היום' },
      { name: 'פרנס', reason: 'פרנס לכבוד החג' },
      { name: 'הודעות', reason: 'הודעות לחג' },
    ],
    suggestedWith: ['bt-0017-4d5e-b6f7-a8b9c0d1e217', 'bt-0020-4d5e-b6f7-a8b9c0d1e220'],
    settings: settings('elegant'),
    widgets: [
      w('w18-01', 'MoadWidget',       'Elegant',         0,  0, 8, 28, moad(),   WS_TIME),
      w('w18-clk','ClockWidget',      CLOCK_TPL.elegant, 8,  0, 8, 14, {},       WS_CLOCK),
      w('w18-02', 'ZmanimWidget',     'Normal',          8, 14, 8, 14, zTL(),    WS_TIME),
      w('w18-03', 'ParnasWidget',     'Tiles',          16,  0, 8, 14, parnas(), WS_PARNAS),
      w('w18-04', 'NoticeBoardWidget','Board',          16, 14, 8, 14, noti(),   WS_NOTICE_EL),
    ],
  },

  {
    id: 'bt-0019-4d5e-b6f7-a8b9c0d1e219',
    name: 'מועד מינימלי',
    category: 'holiday',
    concept: 'שלוש עמודות מינימליות — זמני מועד ממורכז, זמנים, ולוח ספוטלייט.',
    whenToUse: 'מסכים שרוצים עיצוב שקט ומינימלי לחג, ללא עומס ויזואלי.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון שיש לבן בראש עמודת הזמנים' },
      { name: 'מועד ממורכז', reason: 'זמנים גדולים וברורים' },
      { name: 'זמנים', reason: 'רשימת זמנים' },
      { name: 'הודעות ספוטלייט', reason: 'הודעה מרכזית מוחלפת' },
    ],
    suggestedWith: ['bt-0013-4d5e-b6f7-a8b9c0d1e213', 'bt-0017-4d5e-b6f7-a8b9c0d1e217'],
    settings: settings('minimal'),
    widgets: [
      w('w19-01', 'MoadWidget',       'Centered',         0,  0, 8, 28, moad(), WS_TIME),
      w('w19-clk','ClockWidget',      CLOCK_TPL.minimal,  8,  0, 8, 14, {},     WS_CLOCK),
      w('w19-02', 'ZmanimWidget',     'Normal',           8, 14, 8, 14, zTL(),  WS_TIME),
      w('w19-03', 'NoticeBoardWidget','Spotlight',       16,  0, 8, 28, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0020-4d5e-b6f7-a8b9c0d1e220',
    name: 'מועד חגיגי',
    category: 'holiday',
    concept: 'לוח חגיגי עם רשת ימים רחבה לכל ימי החג, זמנים, תפילות, פרנס והודעות.',
    whenToUse: 'חגים רב-ימיים כמו פסח, סוכות וחנוכה — הלוח מציג את כל ימי החג.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון זהב אלגנטי בין רשת ימי החג לזמנים' },
      { name: 'מועד רשת ימים', reason: 'כרטיס לכל יום חג' },
      { name: 'זמנים קבוע', reason: 'זמנים קומפקטיים' },
      { name: 'תפילות', reason: 'שעות תפילת החג' },
      { name: 'פרנס', reason: 'אריחי פרנסים' },
      { name: 'הודעות', reason: 'הודעות בסגנון אורנמנט' },
    ],
    suggestedWith: ['bt-0017-4d5e-b6f7-a8b9c0d1e217', 'bt-0018-4d5e-b6f7-a8b9c0d1e218'],
    settings: settings('elegant'),
    widgets: [
      // MoadWidget מצומצם ל-w:8 כדי לפנות עמודת שעון+זמנים ברוחב 4
      w('w20-01', 'MoadWidget',       'DayGrid',         0,  0,  8, 28, moad(),   WS_TIME),
      w('w20-clk','ClockWidget',      CLOCK_TPL.elegant, 8,  0,  4, 14, {},       WS_CLOCK),
      w('w20-02', 'ZmanimWidget',     'Normal',          8, 14,  4, 14, zTL(),    WS_TIME),
      w('w20-03', 'TefilotWidget',    'Modern',         12,  0,  6, 14, tef(),    WS_TIME),
      w('w20-04', 'ParnasWidget',     'Tiles',          12, 14,  6, 14, parnas(), WS_PARNAS),
      // הודעות מורחבות לגובה מלא
      w('w20-05', 'NoticeBoardWidget','Ornament',       18,  0,  6, 28, noti(),   WS_NOTICE_EL),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // D. יום + שבת משולב (T21–T23)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0021-4d5e-b6f7-a8b9c0d1e221',
    name: 'משולב — ציר זמן ושבת',
    category: 'combined',
    concept: 'שילוב של ציר זמן יומי עם פרטי שבת ותפילות — מתאים לסוף שבוע ולימי שישי.',
    whenToUse: 'ימי שישי ובוקר שבת שרוצים להציג גם מידע יום-יום וגם מידע לשבת הקרובה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון דיגיטלי בראש עמודת הזמנים' },
      { name: 'ציר זמן', reason: 'זמני היום' },
      { name: 'שבת', reason: 'פרטי שבת הקרובה' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות שבת' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0010-4d5e-b6f7-a8b9c0d1e210'],
    settings: settings('neutral'),
    widgets: [
      w('w21-clk','ClockWidget',      CLOCK_TPL.elegant, 0,  0, 8, 14, {},    WS_CLOCK),
      w('w21-01', 'ZmanimWidget',     'Normal',          0, 14, 8, 14, zTL(), WS_TIME),
      w('w21-02', 'ShabbatWidget',    'Card',            8,  0, 8, 28, s(),   WS_TIME),
      w('w21-03', 'TefilotWidget',    'Modern',         16,  0, 8, 14, tef(),  WS_TIME),
      w('w21-04', 'NoticeBoardWidget','Board',          16, 14, 8, 14, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0022-4d5e-b6f7-a8b9c0d1e222',
    name: 'משולב — אמצע שבוע',
    category: 'combined',
    concept: 'לוח שנראה יפה לאורך כל השבוע — זמנים ותפילות עם שבת ושמחות לצד פרנס.',
    whenToUse: 'לוח קבוע שמציגים כל השבוע ומתאים לימי ראשון עד חמישי.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון בחצי העליון של עמודת הפרנס' },
      { name: 'זמנים', reason: 'זמני היום' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
      { name: 'שבת', reason: 'תזכורת לשבת הקרובה' },
      { name: 'שמחות', reason: 'שמחות קהילה' },
      { name: 'פרנס שבועי', reason: 'תורן השבוע' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0005-4d5e-b6f7-a8b9c0d1e205'],
    settings: settings('neutral'),
    widgets: [
      w('w22-01', 'ZmanimWidget',     'Normal',          0,  0, 6, 28, zTL(),    WS_TIME),
      w('w22-02', 'TefilotWidget',    'Cards',           6,  0, 6, 14, tef(),    WS_TIME),
      w('w22-03', 'NoticeBoardWidget','Callout',         6, 14, 6, 14, noti(),   WS_NOTICE),
      w('w22-04', 'ShabbatWidget',    'Times',          12,  0, 6, 14, s(),      WS_TIME),
      w('w22-05', 'SimchaWidget',     'List',           12, 14, 6, 14, simc(),   WS_COMMUNITY),
      w('w22-clk','ClockWidget',      CLOCK_TPL.neutral, 18,  0, 6, 14, {},      WS_CLOCK),
      w('w22-06', 'ParnasWidget',     'WeekAhead',       18, 14, 6, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0023-4d5e-b6f7-a8b9c0d1e223',
    name: 'משולב — ערב שבת',
    category: 'combined',
    concept: 'לוח מיוחד לערב שבת — שבת במרכז ובולטת, תפילות, זמנים, הודעות ופרנס שבועי.',
    whenToUse: 'יום שישי בצהריים ואחה"צ — כאשר שבת כבר קרובה ורוצים להדגיש את כניסתה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון עץ קלאסי בשורה התחתונה לצד זמנים' },
      { name: 'שבת', reason: 'כרטיס שבת גדול ובולט' },
      { name: 'תפילות', reason: 'תפילת ערב שבת' },
      { name: 'זמנים', reason: 'ציר זמן לפני כניסת שבת' },
      { name: 'הודעות', reason: 'הודעות לשבת' },
      { name: 'פרנס שבועי', reason: 'תורן השבוע' },
    ],
    suggestedWith: ['bt-0010-4d5e-b6f7-a8b9c0d1e210', 'bt-0012-4d5e-b6f7-a8b9c0d1e212'],
    settings: settings('classic'),
    widgets: [
      w('w23-01', 'ShabbatWidget',    'Card',             0,  0,  8, 28, s(),     WS_TIME),
      w('w23-02', 'TefilotWidget',    'Modern',           8,  0,  8, 14, tef(),   WS_TIME),
      // ZmanimWidget מצומצם ל-w:4; שעון ממלא את ה-4 העמודות הנוספות בשורה התחתונה
      w('w23-03', 'ZmanimWidget',     'Normal',           8, 14,  4, 14, zTL(),   WS_TIME),
      w('w23-clk','ClockWidget',      CLOCK_TPL.classic, 12, 14,  4, 14, {},      WS_CLOCK),
      w('w23-04', 'NoticeBoardWidget','Board',           16,  0,  8, 14, noti(),  WS_NOTICE_EL),
      w('w23-06', 'ParnasWidget',     'WeekAhead',       16, 14,  8, 14, parnas(), WS_PARNAS),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // E. לוחות מיוחדים (T24–T28)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0024-4d5e-b6f7-a8b9c0d1e224',
    name: 'לוח ספונסרים שנתיים',
    category: 'special',
    concept: 'לוח המוקדש לפרסום ספונסרים ותורמי השנה — פרנסים שנתיים תופסים את עיקר המסך.',
    whenToUse: 'בבתי כנסת עם מערכת ספונסרים ענפה שרוצים להציג יקרא ולשם לאורך כל השנה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון עץ קלאסי בין רשת הספונסרים לזמנים' },
      { name: 'פרנסים שנתיים', reason: 'רשת כל הספונסרים לשנה' },
      { name: 'זמנים קבוע', reason: 'זמנים קומפקטיים בצד' },
      { name: 'שבת', reason: 'זמני שבת' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
    ],
    suggestedWith: ['bt-0016-4d5e-b6f7-a8b9c0d1e216', 'bt-0011-4d5e-b6f7-a8b9c0d1e211'],
    settings: settings('classic'),
    widgets: [
      // ParnasWidget מצומצם ל-w:8 כדי לפנות עמודת שעון+זמנים ברוחב 4
      w('w24-01', 'ParnasWidget',     'WeeklyYear',      0,  0,  8, 28, parnas(), WS_PARNAS),
      w('w24-clk','ClockWidget',      CLOCK_TPL.classic, 8,  0,  4, 14, {},       WS_CLOCK),
      w('w24-02', 'ZmanimWidget',     'Normal',          8, 14,  4, 14, zTL(),    WS_TIME),
      w('w24-03', 'ShabbatWidget',    'Times',          12,  0,  6, 14, s(),      WS_TIME),
      w('w24-04', 'TefilotWidget',    'Modern',         12, 14,  6, 14, tef(),    WS_TIME),
      // הודעות מורחבות לגובה מלא
      w('w24-05', 'NoticeBoardWidget','Board',          18,  0,  6, 28, noti(),   WS_NOTICE),
    ],
  },

  {
    id: 'bt-0025-4d5e-b6f7-a8b9c0d1e225',
    name: 'לוח קהילה מלא',
    category: 'special',
    concept: 'לוח קהילתי מקיף — אנשי קשר, חירום, שמחות, הודעות, פרנס וזמנים.',
    whenToUse: 'לוח שנועד בעיקר לחיזוק הקהילה — להכיר אנשים, לדעת למי לפנות ולשתף שמחות.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון כהה ומודרני בראש עמודת אנשי הקשר' },
      { name: 'אנשי קשר', reason: 'גבאים ואנשי הנהלה' },
      { name: 'כוננות חירום', reason: 'אנשי חירום זמינים' },
      { name: 'שמחות', reason: 'שמחות הקהילה' },
      { name: 'הודעות', reason: 'הודעות בציר זמן' },
      { name: 'פרנס', reason: 'אריחי פרנסים' },
      { name: 'זמנים קבוע', reason: 'זמנים קומפקטיים' },
    ],
    suggestedWith: ['bt-0007-4d5e-b6f7-a8b9c0d1e207', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('modern'),
    widgets: [
      w('w25-clk','ClockWidget',      CLOCK_TPL.modern,  0,  0, 6, 14, {},      WS_CLOCK),
      w('w25-01', 'ContactsWidget',   'List',            0, 14, 6, 14, cont(),  WS_COMMUNITY),
      w('w25-02', 'EmergencyWidget',  'List',            6,  0, 6, 14, emer(),  WS_COMMUNITY),
      w('w25-03', 'SimchaWidget',     'Cards',           6, 14, 6, 14, simc(),  WS_COMMUNITY),
      w('w25-04', 'NoticeBoardWidget','Board',          12,  0, 6, 28, noti(),  WS_NOTICE),
      w('w25-05', 'ParnasWidget',     'Tiles',          18,  0, 6, 14, parnas(), WS_PARNAS),
      w('w25-06', 'ZmanimWidget',     'Normal',         18, 14, 6, 14, zTL(),   WS_TIME),
    ],
  },

  {
    id: 'bt-0026-4d5e-b6f7-a8b9c0d1e226',
    name: 'לוח מודעות מרכזי',
    category: 'special',
    concept: 'ספוטלייט ענק של הודעות במרכז — הודעה אחת בכל פעם, ברורה וקריאה מרחוק.',
    whenToUse: 'לובי כניסה, אולם רב-שימושי או כל מקום שרוצים שההודעות יהיו הדבר הראשון שרואים.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון כהה ומודרני בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'בעמודה צרה' },
      { name: 'הודעות ספוטלייט', reason: 'הודעה גדולה מרכזית' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'אנשי קשר', reason: 'למי לפנות' },
    ],
    suggestedWith: ['bt-0004-4d5e-b6f7-a8b9c0d1e204', 'bt-0025-4d5e-b6f7-a8b9c0d1e225'],
    settings: settings('modern'),
    widgets: [
      w('w26-clk','ClockWidget',      CLOCK_TPL.modern,  0,  0,  6, 14, {},    WS_CLOCK),
      w('w26-01', 'ZmanimWidget',     'Normal',          0, 14,  6, 14, zTL(), WS_TIME),
      w('w26-02', 'NoticeBoardWidget','Spotlight',       6,  0, 12, 28, noti(), WS_NOTICE),
      w('w26-03', 'TefilotWidget',    'Modern',         18,  0,  6, 14, tef(),  WS_TIME),
      w('w26-04', 'ContactsWidget',  'List',            18, 14,  6, 14, cont(), WS_COMMUNITY),
    ],
  },

  {
    id: 'bt-0027-4d5e-b6f7-a8b9c0d1e227',
    name: 'לוח כספי',
    category: 'special',
    concept: 'לוח שמציג שקיפות כלכלית — יעדי תקציב, ניתוח הוצאות — לצד ציר זמן ותפילות.',
    whenToUse: 'ישיבות ועד, ימי הורים או תצוגה קבועה שמשקפת את הפעילות הכלכלית של בית הכנסת.',
    includedWidgets: [
      { name: 'יעדים כספיים', reason: 'מד עמידה ביעד' },
      { name: 'ניתוח הוצאות', reason: 'גרף עמודות לאורך זמן' },
      { name: 'ציר זמן', reason: 'זמני היום' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'הודעות', reason: 'הודעות קהילה' },
    ],
    suggestedWith: ['bt-0009-4d5e-b6f7-a8b9c0d1e209', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('neutral'),
    widgets: [
      w('w27-01', 'FinancialGaugeWidget',   'Gauge',     0,  0, 8, 14, fin(),  WS_FINANCIAL),
      w('w27-02', 'ExpensesAnalyticsWidget','Bar',       0, 14, 8, 14, exp(),  WS_FINANCIAL),
      w('w27-03', 'ZmanimWidget',           'Normal',    8,  0, 8, 28, zTL(),  WS_TIME),
      w('w27-04', 'TefilotWidget',          'Modern',   16,  0, 8, 14, tef(),  WS_TIME),
      w('w27-05', 'NoticeBoardWidget',      'Board',    16, 14, 8, 14, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0028-4d5e-b6f7-a8b9c0d1e228',
    name: 'לוח עם לוגו ותמונה',
    category: 'special',
    concept: 'לוח שמשלב תמונה או לוגו בית הכנסת — עם הודעות, שבת, זמנים ותפילות.',
    whenToUse: 'כאשר רוצים ללוח זהות ויזואלית חזקה של בית הכנסת עם לוגו או תמונת מקום.',
    includedWidgets: [
      // שעון מחליף את תמונת הפלייסהולדר — ניתן להוסיף תמונה/לוגו בנפרד בעתיד
      { name: 'שעון', reason: 'שעון דיגיטלי במקום תמונת הפלייסהולדר' },
      { name: 'זמנים קבוע', reason: 'זמנים קומפקטיים' },
      { name: 'הודעות', reason: 'הודעות באורנמנט' },
      { name: 'שבת', reason: 'זמני שבת' },
      { name: 'תפילות', reason: 'שעות תפילה' },
    ],
    suggestedWith: ['bt-0032-4d5e-b6f7-a8b9c0d1e232', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('neutral'),
    widgets: [
      // ClockWidget מחליף את ה-ImageWidget הריק
      w('w28-clk','ClockWidget',      CLOCK_TPL.neutral, 0,  0, 8, 14, {},    WS_CLOCK),
      w('w28-02', 'ZmanimWidget',     'Normal',          0, 14, 8, 14, zTL(), WS_TIME),
      w('w28-03', 'NoticeBoardWidget','Ornament',        8,  0, 8, 28, noti(), WS_NOTICE),
      w('w28-04', 'ShabbatWidget',    'Times',          16,  0, 8, 14, s(),    WS_TIME),
      w('w28-05', 'TefilotWidget',    'Modern',         16, 14, 8, 14, tef(),  WS_TIME),
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // F. וריאציות עיצוב (T29–T32)
  // ─────────────────────────────────────────────────────────────────────────

  {
    id: 'bt-0029-4d5e-b6f7-a8b9c0d1e229',
    name: 'וריאציה — מינימלי יום חול',
    category: 'design',
    concept: 'שלוש עמודות מינימליות לגמרי — זמנים, תפילות, הודעות. ללא עומס.',
    whenToUse: 'בית כנסת עם אסתטיקה מינימלית, מודרנית ונקייה.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון שיש לבן נקי בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'רשימה נקייה' },
      { name: 'תפילות', reason: 'שעות תפילה פשוטות' },
      { name: 'הודעות מינימלי', reason: 'קו צבעוני עליון לכל הודעה' },
    ],
    suggestedWith: ['bt-0030-4d5e-b6f7-a8b9c0d1e230', 'bt-0013-4d5e-b6f7-a8b9c0d1e213'],
    settings: settings('minimal'),
    widgets: [
      w('w29-clk','ClockWidget',      CLOCK_TPL.minimal, 0,  0, 8, 14, {},    WS_CLOCK),
      w('w29-01', 'ZmanimWidget',     'Normal',          0, 14, 8, 14, zTL(), WS_TIME),
      w('w29-02', 'TefilotWidget',    'Modern',          8,  0, 8, 28, tef(),  WS_TIME),
      w('w29-03', 'NoticeBoardWidget','Minimal',        16,  0, 8, 28, noti(), WS_NOTICE),
    ],
  },

  {
    id: 'bt-0030-4d5e-b6f7-a8b9c0d1e230',
    name: 'וריאציה — מינימלי שבת',
    category: 'design',
    concept: 'שבת, זמנים והודעות בשלוש עמודות נקיות — פרנס בפינה. עיצוב נקי ומרגיע.',
    whenToUse: 'בית כנסת עם עיצוב מינימלי שרוצה לוח שבת ללא עומס.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון שיש לבן נקי בראש עמודת הזמנים' },
      { name: 'שבת', reason: 'עיצוב מודרני נקי' },
      { name: 'זמנים', reason: 'רשימה פשוטה' },
      { name: 'הודעות מינימלי', reason: 'עיצוב נקי' },
      { name: 'פרנס', reason: 'אריחים קומפקטיים' },
    ],
    suggestedWith: ['bt-0029-4d5e-b6f7-a8b9c0d1e229', 'bt-0013-4d5e-b6f7-a8b9c0d1e213'],
    settings: settings('minimal'),
    widgets: [
      w('w30-01', 'ShabbatWidget',    'Modern',           0,  0, 8, 28, s(),      WS_TIME),
      w('w30-clk','ClockWidget',      CLOCK_TPL.minimal,  8,  0, 8, 14, {},       WS_CLOCK),
      w('w30-02', 'ZmanimWidget',     'Normal',           8, 14, 8, 14, zTL(),    WS_TIME),
      w('w30-03', 'NoticeBoardWidget','Minimal',         16,  0, 8, 14, noti(),   WS_NOTICE),
      w('w30-04', 'ParnasWidget',     'Tiles',           16, 14, 8, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0031-4d5e-b6f7-a8b9c0d1e231',
    name: 'וריאציה — קומפקטי עם מזג אוויר',
    category: 'design',
    concept: 'ארבע עמודות קומפקטיות — זמנים, שבת ומזג אוויר, תפילות בעמודות, הודעות ופרנס.',
    whenToUse: 'מסכים שרוצים לשלב מידע מזג אוויר לצד מידע בית הכנסת.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון כהה ומודרני בראש עמודת הזמנים' },
      { name: 'זמנים', reason: 'עמודה צרה' },
      { name: 'שבת', reason: 'זמני שבת' },
      { name: 'מזג אוויר', reason: 'תחזית מזג אוויר' },
      { name: 'תפילות', reason: 'עמודות שחרית/מנחה/ערבית' },
      { name: 'הודעות', reason: 'הודעות כלליות' },
      { name: 'פרנס', reason: 'אריחים' },
    ],
    suggestedWith: ['bt-0001-4d5e-b6f7-a8b9c0d1e201', 'bt-0029-4d5e-b6f7-a8b9c0d1e229'],
    settings: settings('modern'),
    widgets: [
      w('w31-clk','ClockWidget',      CLOCK_TPL.modern,  0,  0, 6, 14, {},    WS_CLOCK),
      w('w31-01', 'ZmanimWidget',     'Normal',          0, 14, 6, 14, zTL(), WS_TIME),
      w('w31-02', 'ShabbatWidget',    'Times',           6,  0, 4, 14, s(),   WS_TIME),
      w('w31-03', 'WeatherWidget',    'Card',            6, 14, 4, 14, { title: 'מזג האוויר', showTitle: false }, WS_TIME),
      w('w31-04', 'TefilotWidget',    'Columns',        10,  0, 8, 28, tef(),  WS_TIME),
      w('w31-05', 'NoticeBoardWidget','Board',          18,  0, 6, 14, noti(), WS_NOTICE),
      w('w31-06', 'ParnasWidget',     'Tiles',          18, 14, 6, 14, parnas(), WS_PARNAS),
    ],
  },

  {
    id: 'bt-0032-4d5e-b6f7-a8b9c0d1e232',
    name: 'וריאציה — עם טקסט ותמונה',
    category: 'design',
    concept: 'לוח שמשלב תוכן טקסטואלי מעוצב עם תמונה/לוגו — לצד זמנים, תפילות, הודעות ושבת.',
    whenToUse: 'בית כנסת שרוצה להציג ציטוט יפה, פסוק או מסר מיוחד בלוח לצד המידע השוטף.',
    includedWidgets: [
      { name: 'שעון', reason: 'שעון דיגיטלי מחליף את תמונת הפלייסהולדר' },
      { name: 'זמנים', reason: 'רשימת זמנים' },
      { name: 'תפילות', reason: 'שעות תפילה' },
      { name: 'טקסט מעוצב', reason: 'ציטוט או מסר מיוחד' },
      { name: 'הודעות', reason: 'הודעות כלליות' },
      { name: 'שבת', reason: 'תזכורת שבת' },
      { name: 'פרנס', reason: 'אריחים' },
    ],
    suggestedWith: ['bt-0028-4d5e-b6f7-a8b9c0d1e228', 'bt-0001-4d5e-b6f7-a8b9c0d1e201'],
    settings: settings('neutral'),
    widgets: [
      w('w32-01', 'ZmanimWidget',     'Normal',          0,  0, 6, 28, zTL(),  WS_TIME),
      w('w32-02', 'TefilotWidget',    'Modern',          6,  0, 6, 14, tef(),  WS_TIME),
      w('w32-03', 'TextWidget',       'Simple',          6, 14, 6, 14,
        { text: '<div style="text-align:center;padding:24px;color:#1e3a5f;font-size:28px;font-weight:700;line-height:1.8;">✦<br/>נא לשמור על קדושת המקום<br/>✦</div>' },
        WS_IMAGE,
      ),
      w('w32-04', 'NoticeBoardWidget','Board',          12,  0, 6, 14, noti(), WS_NOTICE),
      w('w32-05', 'ShabbatWidget',    'Times',          12, 14, 6, 14, s(),    WS_TIME),
      w('w32-06', 'ParnasWidget',     'Tiles',          18,  0, 6, 14, parnas(), WS_PARNAS),
      // ClockWidget מחליף את ה-ImageWidget הריק
      w('w32-clk','ClockWidget',      CLOCK_TPL.neutral, 18, 14, 6, 14, {},    WS_CLOCK),
    ],
  },
];

// ── Category labels (for UI filter chips) ─────────────────────────────────
export const CATEGORY_LABELS = {
  weekday:  'יום חול',
  shabbat:  'שבת',
  holiday:  'מועדים',
  combined: 'משולב',
  special:  'מיוחדים',
  design:   'וריאציות',
};
